/**
 * ManagerLogin - phone + SMS code (and optional PIN) sign-in for the Manager
 * Assistant. Mirrors the client app login UX in the manager app's green / v4
 * style.
 *
 * Flow:
 *   - 'pin'   : shown first when a remembered phone + cached PIN exist. 4-digit
 *               PIN -> loginWithPin -> /pa. Falls back to SMS.
 *   - 'phone' : phone entry -> requestCode -> 'code'.
 *   - 'code'  : 6-digit SMS code -> verifyCode. On success, if no PIN is cached
 *               yet, offers to set one ('setpin'); otherwise -> /pa.
 *   - 'setpin': optional 4-digit PIN setup -> setPin -> /pa (skippable).
 */
import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, Loader2, ArrowLeft, KeyRound, Lock, Mail } from 'lucide-react';
import { toast } from 'sonner';
import {
  requestCode,
  verifyCode,
  loginWithPin,
  loginWithEmail,
  setPin as setPinRemote,
  getRememberedPhone,
  getHasPinCached,
} from '@/services/api/manager-auth';

type Step = 'phone' | 'code' | 'pin' | 'setpin' | 'email';

export default function ManagerLogin() {
  const navigate = useNavigate();

  const remembered = getRememberedPhone();
  const canUsePin = !!remembered && getHasPinCached();

  const [step, setStep] = useState<Step>(canUsePin ? 'pin' : 'phone');
  const [phone, setPhone] = useState(remembered || '');
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const boxRefs = useRef<(HTMLInputElement | null)[]>([]);
  const pinRef = useRef<HTMLInputElement | null>(null);
  const newPinRef = useRef<HTMLInputElement | null>(null);
  const code = digits.join('');

  useEffect(() => {
    if (step === 'code') boxRefs.current[0]?.focus();
    if (step === 'pin') pinRef.current?.focus();
    if (step === 'setpin') newPinRef.current?.focus();
  }, [step]);

  // ---- PIN (returning user) ----------------------------------------------
  async function onPinSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!/^\d{4}$/.test(pin) || busy) return;
    setBusy(true);
    try {
      await loginWithPin(phone, pin);
      navigate('/pa', { replace: true });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Invalid PIN');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  function fallbackToSms() {
    setPin('');
    setStep('phone');
  }

  // ---- SMS code boxes -----------------------------------------------------
  function setDigitAt(i: number, v: string) {
    const ch = v.replace(/\D/g, '').slice(-1);
    setDigits(prev => {
      const next = [...prev];
      next[i] = ch;
      return next;
    });
    if (ch && i < 5) boxRefs.current[i + 1]?.focus();
  }

  function onBoxKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      boxRefs.current[i - 1]?.focus();
      setDigits(prev => { const n = [...prev]; n[i - 1] = ''; return n; });
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      boxRefs.current[i - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && i < 5) {
      boxRefs.current[i + 1]?.focus();
      e.preventDefault();
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const last = Math.min(pasted.length, 5);
    boxRefs.current[last]?.focus();
  }

  // ---- resend cooldown ----------------------------------------------------
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  async function onSendCode(e?: React.FormEvent) {
    e?.preventDefault();
    if (!phone.trim() || busy) return;
    setBusy(true);
    try {
      await requestCode(phone.trim());
      setStep('code');
      setDigits(['', '', '', '', '', '']);
      setCooldown(60);
      toast.success('Code sent - check your messages');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to send code');
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e?: React.FormEvent) {
    e?.preventDefault();
    if (!/^\d{6}$/.test(code) || busy) return;
    setBusy(true);
    try {
      await verifyCode(phone.trim(), code);
      // First SMS login with no PIN set yet -> offer to set one.
      if (!getHasPinCached()) {
        setStep('setpin');
      } else {
        navigate('/pa', { replace: true });
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Invalid code');
      setDigits(['', '', '', '', '', '']);
      boxRefs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (step === 'code' && /^\d{6}$/.test(code) && !busy) {
      onVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // ---- optional PIN setup -------------------------------------------------
  async function onSetPin(e?: React.FormEvent) {
    e?.preventDefault();
    if (!/^\d{4}$/.test(newPin) || busy) return;
    setBusy(true);
    try {
      await setPinRemote(newPin);
      toast.success('PIN set - you can use it next time');
      navigate('/pa', { replace: true });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not set PIN');
    } finally {
      setBusy(false);
    }
  }

  async function onEmailLogin(e?: React.FormEvent) {
    e?.preventDefault();
    if (!email.trim() || !password || busy) return;
    setBusy(true);
    try {
      await loginWithEmail(email.trim(), password);
      navigate('/pa', { replace: true });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Sign-in failed');
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  const isPhoneValid = phone.replace(/[^\d+]/g, '').length >= 8;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-6 pt-16 pb-8">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-[var(--v4-accent)] text-white mb-4 shadow-sm">
            <Lock className="w-7 h-7" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Manager Assistant</h1>
          <p className="text-sm text-muted-foreground mt-1.5 text-center">
            {step === 'phone'  && 'Sign in to your firm dashboard'}
            {step === 'email'  && 'Sign in with your firm email and password'}
            {step === 'code'   && `Enter the 6-digit code sent to ${phone}`}
            {step === 'pin'    && 'Enter your 4-digit PIN'}
            {step === 'setpin' && 'Set a 4-digit PIN for faster access'}
          </p>
        </div>

        {step === 'pin' && (
          <form onSubmit={onPinSubmit} className="space-y-4">
            <div className="text-center mb-2">
              <p className="text-xs text-muted-foreground">{phone}</p>
            </div>
            <div className="relative">
              <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={pinRef}
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                placeholder="••••"
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                disabled={busy}
                className="w-full pl-10 pr-3 py-3.5 rounded-xl border border-border bg-muted text-foreground text-center text-2xl tracking-[0.5em] placeholder:tracking-normal placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-[var(--v4-accent)] focus:border-transparent transition"
              />
            </div>
            <button
              type="submit"
              disabled={!/^\d{4}$/.test(pin) || busy}
              className="w-full py-3.5 rounded-xl bg-[var(--v4-accent)] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition shadow-sm hover:shadow"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Unlock <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <button
              type="button"
              onClick={fallbackToSms}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition"
            >
              Use SMS code instead
            </button>
          </form>
        )}

        {step === 'phone' && (
          <form onSubmit={onSendCode} className="space-y-4">
            <div className="relative">
              <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="04XX XXX XXX"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                disabled={busy}
                className="w-full pl-10 pr-3 py-3.5 rounded-xl border border-border bg-muted text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-[var(--v4-accent)] focus:border-transparent transition"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={!isPhoneValid || busy}
              className="w-full py-3.5 rounded-xl bg-[var(--v4-accent)] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition shadow-sm hover:shadow"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Send code <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <button
              type="button"
              onClick={() => setStep('email')}
              className="w-full text-center text-sm font-medium text-[var(--v4-accent)] hover:underline"
            >
              Sign in with email instead
            </button>
            <p className="text-xs text-muted-foreground text-center mt-4 leading-relaxed">
              Your phone number must already be registered with your firm.
              <br />
              No access? Ask an administrator to add you.
            </p>
          </form>
        )}

        {step === 'email' && (
          <form onSubmit={onEmailLogin} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@firm.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={busy}
                autoFocus
                className="w-full pl-10 pr-3 py-3.5 rounded-xl border border-border bg-muted text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-[var(--v4-accent)] focus:border-transparent transition"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={busy}
                className="w-full pl-10 pr-3 py-3.5 rounded-xl border border-border bg-muted text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-[var(--v4-accent)] focus:border-transparent transition"
              />
            </div>
            <button
              type="submit"
              disabled={!email.trim() || !password || busy}
              className="w-full py-3.5 rounded-xl bg-[var(--v4-accent)] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition shadow-sm hover:shadow"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <button
              type="button"
              onClick={() => { setStep('phone'); setPassword(''); }}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition inline-flex items-center justify-center gap-1.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Use phone instead
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={onVerify} className="space-y-5">
            <div className="flex justify-between gap-2 sm:gap-3" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => (boxRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={1}
                  value={d}
                  onChange={e => setDigitAt(i, e.target.value)}
                  onKeyDown={e => onBoxKeyDown(i, e)}
                  onFocus={e => e.currentTarget.select()}
                  disabled={busy}
                  className="w-full aspect-square min-w-0 rounded-2xl border-2 border-border bg-muted text-foreground text-center text-2xl font-semibold focus:outline-none focus:border-[var(--v4-accent)] focus:ring-2 focus:ring-[var(--v4-accent)]/30 transition disabled:opacity-60"
                />
              ))}
            </div>

            {busy && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
              </div>
            )}

            <div className="flex items-center justify-between pt-1 text-sm">
              <button
                type="button"
                onClick={() => { setStep('phone'); setDigits(['', '', '', '', '', '']); }}
                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Change number
              </button>
              <button
                type="button"
                onClick={() => onSendCode()}
                disabled={cooldown > 0 || busy}
                className="text-muted-foreground hover:text-foreground disabled:opacity-50 transition"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {step === 'setpin' && (
          <form onSubmit={onSetPin} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={newPinRef}
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                placeholder="••••"
                value={newPin}
                onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                disabled={busy}
                className="w-full pl-10 pr-3 py-3.5 rounded-xl border border-border bg-muted text-foreground text-center text-2xl tracking-[0.5em] placeholder:tracking-normal placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-[var(--v4-accent)] focus:border-transparent transition"
              />
            </div>
            <button
              type="submit"
              disabled={!/^\d{4}$/.test(newPin) || busy}
              className="w-full py-3.5 rounded-xl bg-[var(--v4-accent)] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition shadow-sm hover:shadow"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Save PIN <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <button
              type="button"
              onClick={() => navigate('/pa', { replace: true })}
              disabled={busy}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition disabled:opacity-50"
            >
              Skip for now
            </button>
          </form>
        )}
      </div>

      <div className="mt-auto pt-12">
        <p className="text-xs text-muted-foreground">Powered by <span className="font-medium">RoniDoc.ai</span></p>
      </div>
    </div>
  );
}
